import React, { useEffect, useState, useMemo } from "react"
import { db } from "../../utils/firebase"
import "./internal.scss"

/**
 * UI components
 */
import Tile from "../../components/structure/tile/tile"
import Title from "../../components/structure/title/title"
import Staff from "../../components/internal/staff/staff"
import Button from "../../components/ui/button/button"
import Input from "../../components/ui/inputs/input"
import { PlusIcon, SearchIcon } from "../../utils/svgs"
import AddAgent from "./add/add"

/**
 * Functional component to return a full staff list fo assigning targets to
 */
function Internal() {
	const [users, setUsers] = useState([])
	const [searchTerm, setSearchTerm] = useState("")
	const [statusFilter, setStatusFilter] = useState("ALL")

	const [adding, setAdding] = useState(false)
	const [addingNewAgent, setAddingNewAgent] = useState(false)

	/**
	 * On component load
	 */
	useEffect(() => {
		/**
		 * Pull a list of all sales agents from the databas
		 */
		const unsubscribe = db
			.collection("users")
			.orderBy("first_name")
			.onSnapshot((userChanges) => {
				/**
				 * Loop over the user documents found
				 */
				userChanges.docChanges().forEach((change) => {
					/**
					 * Agent document added
					 */
					if (change.type === "added") {
						setUsers((users) => [
							...users,
							{
								id: change.doc.id,
								...change.doc.data(),
							},
						])
					}
					/**
					 * Agent document updated
					 */
					if (change.type === "modified") {
						setUsers((users) => {
							let updatedUsers = [...users]
							for (let i in users) {
								if (users[i].id === change.doc.id) {
									updatedUsers[i] = {
										id: change.doc.id,
										...change.doc.data(),
									}
									break
								}
							}
							return updatedUsers
						})
					}
					/**
					 * Agent document removed
					 */
					if (change.type === "removed") {
						setUsers((users) => users.filter((user) => user.id !== change.doc.id))
					}
				})
			})
		/**
		 * Remove the listener when we're done
		 */
		return () => unsubscribe()
	}, [])

	/**
	 * Count how many accounts are currently locked out of the system
	 */
	const lockedCount = useMemo(() => {
		return users.filter((user) => user.account_locked === true).length
	}, [users])

	/**
	 * Filter users based on the search term and the account status filter
	 */
	const filteredUsers = useMemo(() => {
		const search = searchTerm.toLowerCase().trim()

		return users.filter((user) => {
			/**
			 * Check the account status filter first
			 */
			const isLocked = user.account_locked === true
			if (statusFilter === "LOCKED" && !isLocked) return false
			if (statusFilter === "ACTIVE" && isLocked) return false
			/**
			 * Then the search term, if there is one
			 */
			if (!search) return true

			const fullName = `${user.first_name || ""} ${user.last_name || ""}`.toLowerCase()
			const email = (user.email || "").toLowerCase()
			const id = user.id.toLowerCase()

			return fullName.includes(search) || email.includes(search) || id.includes(search)
		})
	}, [users, searchTerm, statusFilter])

	return (
		<Tile fullPage={true}>
			<Title className="flex">
				<h1>System users</h1>

				<div className="flex gap-2">
					<Button
						label="Add agent"
						small={true}
						icon={<PlusIcon />}
						loading={adding}
						loadingText="Adding..."
						onClick={() => setAddingNewAgent(true)}
					/>
				</div>
			</Title>

			{/* Search bar */}
			<div className="users-search-bar">
				<Input
					placeholder="Search users by name, email, or ID..."
					value={searchTerm}
					onChange={setSearchTerm}
					icon={<SearchIcon />}
				/>
				{(searchTerm || statusFilter !== "ALL") && (
					<small className="search-results-count">
						Showing {filteredUsers.length} of {users.length} users
					</small>
				)}

				{/* Account status filter */}
				<div className="users-status-filter">
					{[
						{ key: "ALL", label: `All (${users.length})` },
						{ key: "ACTIVE", label: `Active (${users.length - lockedCount})` },
						{ key: "LOCKED", label: `Locked (${lockedCount})` },
					].map((filter) => (
						<button
							key={filter.key}
							type="button"
							className={["status-filter-btn", statusFilter === filter.key && "is-active"]
								.filter(Boolean)
								.join(" ")}
							onClick={() => setStatusFilter(filter.key)}>
							{filter.label}
						</button>
					))}
				</div>
			</div>

			{/* User cards grid */}
			<div className="users-grid">
				{filteredUsers.map((user) => (
					<Staff
						key={user.id}
						id={user.id}
						details={user}
					/>
				))}

				{filteredUsers.length === 0 && (searchTerm || statusFilter !== "ALL") && (
					<div className="no-results">
						<p>{searchTerm ? `No users found matching "${searchTerm}"` : "No users with that status"}</p>
					</div>
				)}
			</div>

			{/* Are we adding a new agent to the system */}
			{addingNewAgent && <AddAgent close={() => setAddingNewAgent(false)} />}
		</Tile>
	)
}

export default Internal
